// src/features/whatsapp/components/EventMessageEditor.tsx
// ============================================================================
// Editor reutilizable de mensajes automáticos por evento (Inc11). Re-cableado a
// lealify-base (/messages/settings) — reemplaza la config en Firestore. Lo usan
// las 7 páginas de mensaje por evento como wrapper delgado; desde el 24-oct-2026 se
// llega a ellas desde la lista «Mensajes automáticos» (/dashboard/mensajes?evento=…). Variables estilo @nombre (las resuelve el render del worker de base).
// Imágenes adjuntas DIFERIDAS (base envía mensajes de evento solo texto).
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { canUseWhatsapp, getWhatsappAccessState } from '../../../utils/whatsappAccess';
import WhatsAppRequestPanel from '../../../components/whatsapp/WhatsAppRequestPanel';
import { messagesApi, type EventKey, type EventSettings, type EventEmailSettings } from '../../../api/messagesApi';
import { Eye, RotateCcw, Sparkles } from 'lucide-react';
import BrandLoader from '../../../components/common/BrandLoader';
import Toggle from '../../../components/ui/Toggle';
import AlertBox from '../../../components/ui/AlertBox';
import WahaStatusBadge from './WahaStatusBadge';
import SaveBar from './SaveBar';
import StepGuide from './StepGuide';
import RequirementsChecklist from './RequirementsChecklist';
import WhatsAppPreviewBubble from './WhatsAppPreviewBubble';
import VolverAMensajes from './VolverAMensajes';
import { ProLocked } from '../../../components/pro/ProLocked';
import type { ProFeatureKey } from '../../../components/pro/proFeatures';

// Qué explicación PRO corresponde a cada mensaje automático (components/pro/proFeatures.ts).
const PRO_KEY: Record<EventKey, ProFeatureKey> = {
  welcome: 'welcome-message', visit: 'visit-message', referral: 'referral-message',
  feedback_alert: 'feedback-alert', low_balance: 'low-balance-alert',
  membership_expiring: 'membership-notice', membership_expired: 'membership-notice',
};
import ImageUpload from '../../../components/common/ImageUpload';

// aliases = otros nombres que el backend también sustituye por lo mismo (render() de
// eventMessages.js: @marca=@negocio, @total=@saldo…). Plantillas guardadas con ellos (224 de
// saldo bajo traían @total/@marca de fábrica) salían sin reemplazar en la vista previa y parecía
// roto (El Postrecito Feliz, 9-oct-2026): ahora se resuelven y se avisa que también valen.
export interface VarDef { key: string; label: string; example: string; aliases?: string[]; }
export interface ExtraField {
  settingKey: 'alert_phone' | 'threshold';
  kind: 'phone' | 'number';
  label: string;
  help?: string;
  placeholder?: string;
}

interface Props {
  eventKey: EventKey;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  accent?: string;                 // color tailwind para el toggle/acento (ej. 'emerald')
  defaultTemplate: string;
  variables: VarDef[];
  extraField?: ExtraField;
  feature: string;                 // etiqueta del paywall
  stepGuide: { description: string; steps: string[] };
  requirementsReady: string;
  enabledHint?: { on: string; off: string };
  allowImage?: boolean;            // permite adjuntar una imagen al mensaje (welcome)
  // Canal de CORREO del mismo evento (independiente del de WhatsApp: pueden convivir,
  // cada uno con su toggle y su copy). Hoy solo la bienvenida lo enciende; visita/saldo
  // bajo lo heredan pasando esta prop. defaultSubject alimenta el placeholder del asunto.
  emailChannel?: { defaultSubject: string };
  /** (F4 membresías) El canal de correo no necesita WhatsApp activado: un PRO sin WhatsApp ve el editor igual. */
  emailWithoutWhatsapp?: boolean;
}

const EventMessageEditor: React.FC<Props> = ({
  eventKey, title, subtitle, icon, defaultTemplate, variables, extraField, feature, stepGuide, requirementsReady, enabledHint, allowImage, emailChannel, emailWithoutWhatsapp,
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const waAccessState = getWhatsappAccessState(currentUser);
  // Con emailWithoutWhatsapp (avisos de membresía, correo por defecto) basta ser PRO: sin WhatsApp activado el editor se
  // abre igual; el canal WhatsApp queda a la vista pero no se puede encender sin sesión (lo rechaza el backend).
  const hasPremiumAccess = canUseWhatsapp(currentUser) || (!!emailWithoutWhatsapp && !!emailChannel && waAccessState !== 'free');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [template, setTemplate] = useState(defaultTemplate);
  const [extraValue, setExtraValue] = useState<string>('');
  const [images, setImages] = useState<string[]>([]);
  const MAX_IMAGES = 3;
  const [original, setOriginal] = useState<{ enabled: boolean; template: string; extra: string; images: string; email: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [wahaConnected, setWahaConnected] = useState<boolean | null>(null);
  const [isCheckingWaha, setIsCheckingWaha] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // ── Canal (solo si emailChannel): EXCLUYENTE — correo O WhatsApp, nunca ambos
  //    (decisión Carlos 3-ago). El toggle maestro prende/apaga el envío; el canal
  //    decide por dónde sale. En base se persiste como enabled / email.enabled.
  const [channel, setChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailCoverage, setEmailCoverage] = useState<{ total: number; with_email: number } | null>(null);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const emailSnapshot = (ch: string, su: string, te: string) => JSON.stringify({ ch, su, te });

  // ── Cargar config desde base ──
  useEffect(() => {
    if (!hasPremiumAccess) { setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const settings = await messagesApi.getSettings();
        if (!alive) return;
        const s = settings[eventKey] as EventSettings;
        const tpl = s?.template || defaultTemplate;
        const en = s?.enabled === true;
        const extra = extraField
          ? String((extraField.settingKey === 'threshold' ? s?.threshold ?? '' : s?.alert_phone ?? '') ?? '')
          : '';
        const imgs = allowImage
          ? (Array.isArray(s?.images) && s.images.length ? s.images.filter(Boolean) : (s?.image_url ? [s.image_url] : []))
          : [];
        const mail: EventEmailSettings = s?.email || { enabled: false, subject: '', template: '' };
        // Canal excluyente: si el correo está encendido, ese es el canal; el toggle
        // maestro está ON si CUALQUIERA de los dos canales lo está.
        const ch: 'whatsapp' | 'email' = mail.enabled === true ? 'email' : 'whatsapp';
        const masterOn = en || mail.enabled === true;
        setEnabled(masterOn); setChannel(ch); setTemplate(tpl); setExtraValue(extra); setImages(imgs);
        setEmailSubject(mail.subject || ''); setEmailTemplate(mail.template || '');
        setOriginal({ enabled: masterOn, template: tpl, extra, images: JSON.stringify(imgs),
          email: emailSnapshot(ch, mail.subject || '', mail.template || '') });
      } catch (e) {
        console.error('Error cargando config de mensaje:', e);
        setError('No se pudo cargar la configuración.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [hasPremiumAccess, eventKey, defaultTemplate, extraField, allowImage]);

  // ── Estado WAHA del negocio (badge) ──
  useEffect(() => {
    if (!hasPremiumAccess) return;
    let alive = true;
    (async () => {
      setIsCheckingWaha(true);
      try { const st = await messagesApi.whatsappStatus(); if (alive) setWahaConnected(st.connected); }
      catch { if (alive) setWahaConnected(false); }
      finally { if (alive) setIsCheckingWaha(false); }
    })();
    return () => { alive = false; };
  }, [hasPremiumAccess]);

  // ── Preview con variables resueltas a sus ejemplos ──
  const previewMessage = useMemo(() => {
    let msg = template;
    for (const v of variables) for (const k of [v.key, ...(v.aliases || [])]) msg = msg.split(k).join(v.example);
    return msg;
  }, [template, variables]);

  const hasChanges = useMemo(() => {
    if (!original) return false;
    return enabled !== original.enabled || template !== original.template || extraValue !== original.extra
      || JSON.stringify(images) !== original.images
      || (!!emailChannel && emailSnapshot(channel, emailSubject, emailTemplate) !== original.email);
  }, [enabled, template, extraValue, images, original, emailChannel, channel, emailSubject, emailTemplate]);

  const handleSave = useCallback(async () => {
    if (!hasChanges) return;
    setSaving(true); setError(null); setSuccessMessage(null);
    try {
      // Canal excluyente: `enabled` de base = WhatsApp; email.enabled = correo. El toggle
      // maestro reparte según el canal elegido — nunca quedan los dos encendidos.
      const waOn = enabled && (!emailChannel || channel === 'whatsapp');
      const patch: Partial<EventSettings> = { enabled: waOn, template: template.trim() };
      if (extraField) {
        if (extraField.settingKey === 'threshold') patch.threshold = Number(extraValue) || 0;
        else patch.alert_phone = extraValue.trim() || null;
      }
      if (allowImage) { patch.images = images; patch.image_url = images[0] || null; }
      if (emailChannel) patch.email = { enabled: enabled && channel === 'email', subject: emailSubject.trim(), template: emailTemplate.trim() };
      await messagesApi.updateEvent(eventKey, patch);
      setOriginal({ enabled, template: template.trim(), extra: extraValue, images: JSON.stringify(images),
        email: emailSnapshot(channel, emailSubject.trim(), emailTemplate.trim()) });
      setSuccessMessage('Configuración guardada correctamente.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (e) {
      console.error('Error guardando config:', e);
      // 1-oct-2026: el servidor ya no deja ENCENDER por WhatsApp sin la sesión conectada (antes
      // «salía» en silencio sin mandarse). Ese motivo se enseña tal cual.
      const code = (e as { data?: { code?: string } })?.data?.code;
      setError(code === 'wa_not_connected'
        ? 'Conecta tu WhatsApp antes de activarlo: sin él no sale ningún mensaje.'
        : 'No se pudo guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }, [hasChanges, enabled, template, extraValue, images, extraField, allowImage, eventKey, emailChannel, channel, emailSubject, emailTemplate]);

  const insertVariable = useCallback((v: string) => setTemplate((prev) => prev + v), []);
  const handleReset = useCallback(() => setTemplate(defaultTemplate), [defaultTemplate]);
  const insertEmailVariable = useCallback((v: string) => setEmailTemplate((prev) => prev + v), []);

  // ── Cobertura de correo (aviso "X de tus Y clientes tienen correo") ──
  useEffect(() => {
    if (!emailChannel || !hasPremiumAccess) return;
    let alive = true;
    messagesApi.emailCoverage().then((c) => { if (alive) setEmailCoverage(c); }).catch(() => {});
    return () => { alive = false; };
  }, [emailChannel, hasPremiumAccess]);

  // ── Preview del correo con la marca REAL (iframe srcdoc; se pide al servidor) ──
  const loadEmailPreview = useCallback(async () => {
    if (emailPreviewHtml !== null) { setEmailPreviewHtml(null); return; }   // toggle: ocultar
    setEmailPreviewLoading(true);
    try {
      const r = await messagesApi.emailPreview(emailSubject, emailTemplate);
      setEmailPreviewHtml(r.html);
    } catch {
      setError('No se pudo generar el preview del correo.');
    } finally {
      setEmailPreviewLoading(false);
    }
  }, [emailPreviewHtml, emailSubject, emailTemplate]);

  // ── Paywall ──
  if (!hasPremiumAccess) {
    return (
      <Layout>
        <div className="space-y-6 px-2 sm:px-0">
          <div className="animate-fade-in-up">
            <VolverAMensajes />
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">{icon}{title}</h1>
            <p className="mt-1 text-sm text-content-2">{subtitle}</p>
          </div>
          {/* Gratis → la explicación PRO; PRO sin WhatsApp activado → solicitar / en proceso. */}
          {waAccessState === 'free'
            ? <ProLocked feature={PRO_KEY[eventKey]} />
            : <WhatsAppRequestPanel state={waAccessState} feature={feature} />}
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <BrandLoader size={36} />
          <span className="ml-3 text-content-2">Cargando configuración...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* A todo lo ancho como el resto del manager (8-oct-2026); antes max-w-3xl centrado. Lo usan las 5 pantallas de mensajes. */}
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up">
          <div>
            <VolverAMensajes />
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">{icon}{title}</h1>
            <p className="mt-1 text-sm text-content-2">{subtitle}</p>
          </div>
          {(!emailChannel || channel === 'whatsapp') && <WahaStatusBadge connected={wahaConnected} checking={isCheckingWaha} />}
        </div>

        {/* WAHA no conectado (irrelevante si el canal elegido es correo) */}
        {(!emailChannel || channel === 'whatsapp') && wahaConnected === false && !isCheckingWaha && (
          <AlertBox variant="warning" title="WhatsApp no está conectado" className="animate-fade-in-up">
            <p>
              Para que estos mensajes se envíen automáticamente, necesitas tu WhatsApp vinculado.{' '}
              <button onClick={() => navigate('/dashboard/whatsapp')} className="font-medium underline hover:opacity-80">
                Vincular WhatsApp
              </button>
            </p>
          </AlertBox>
        )}

        {successMessage && <AlertBox variant="success" className="animate-fade-in-up">{successMessage}</AlertBox>}
        {error && <AlertBox variant="error" className="animate-fade-in-up">{error}</AlertBox>}

        {/* Card principal */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up">
          {/* Toggle */}
          <div className="px-4 sm:px-6 py-4 border-b border-line">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Sparkles className="h-5 w-5 text-emerald-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-content">Envío automático</p>
                  <p className="text-xs text-content-3">
                    {enabled ? (enabledHint?.on || 'Activado') : (enabledHint?.off || 'Activar para enviar automáticamente')}
                  </p>
                  {enabled && !original?.enabled && wahaConnected === false && (!emailChannel || channel === 'whatsapp') && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Sin WhatsApp conectado no se podrá guardar encendido.{emailChannel ? ' Puedes elegir el correo como canal.' : ''}
                    </p>
                  )}
                </div>
              </div>
              <Toggle checked={enabled} onChange={setEnabled} label={enabled ? 'Desactivar' : 'Activar'} className="ml-4" />
            </div>

            {/* Selector de CANAL — excluyente. Tarjetas tipo radio con badge ACTIVO y la no
                elegida atenuada: la versión anterior (botones segmentados) no dejaba claro
                cuál quedaba encendido (feedback Carlos 3-ago). */}
            {emailChannel && (
              <div className={`mt-4 transition-opacity duration-200 ${!enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <p className="text-xs font-medium text-content-3 mb-2">¿Por dónde sale este mensaje? Elige UN canal — el otro queda apagado:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Canal de envío">
                  <button role="radio" aria-checked={channel === 'whatsapp'} onClick={() => setChannel('whatsapp')}
                    className={`relative text-left rounded-lg border-2 p-3 pr-16 transition-all ${
                      channel === 'whatsapp'
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                        : 'border-line bg-surface hover:bg-surface-2 opacity-60'
                    }`}>
                    {channel === 'whatsapp'
                      ? <span className="absolute top-2.5 right-2.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">ACTIVO</span>
                      : <span className="absolute top-2.5 right-2.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface-2 text-content-3">apagado</span>}
                    <p className={`text-sm font-semibold ${channel === 'whatsapp' ? 'text-content' : 'text-content-2'}`}>
                      <span className={`inline-block w-3.5 h-3.5 mr-1.5 rounded-full border-2 align-[-2px] ${
                        channel === 'whatsapp' ? 'border-emerald-600 bg-emerald-600' : 'border-line'}`} />
                      💬 Por WhatsApp
                    </p>
                    <p className="mt-1 text-xs text-content-3">Llega al WhatsApp del cliente. Usa (y gasta) tu número.</p>
                  </button>
                  <button role="radio" aria-checked={channel === 'email'} onClick={() => setChannel('email')}
                    className={`relative text-left rounded-lg border-2 p-3 pr-16 transition-all ${
                      channel === 'email'
                        ? 'border-sky-500 bg-sky-50 dark:bg-sky-500/10'
                        : 'border-line bg-surface hover:bg-surface-2 opacity-60'
                    }`}>
                    {channel === 'email'
                      ? <span className="absolute top-2.5 right-2.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-600 text-white">ACTIVO</span>
                      : <span className="absolute top-2.5 right-2.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface-2 text-content-3">apagado</span>}
                    <p className={`text-sm font-semibold ${channel === 'email' ? 'text-content' : 'text-content-2'}`}>
                      <span className={`inline-block w-3.5 h-3.5 mr-1.5 rounded-full border-2 align-[-2px] ${
                        channel === 'email' ? 'border-sky-600 bg-sky-600' : 'border-line'}`} />
                      📧 Por correo
                    </p>
                    <p className="mt-1 text-xs text-content-3">Llega a su correo. No toca tu número de WhatsApp.</p>
                  </button>
                </div>
                <p className="mt-2 text-xs font-medium">
                  {channel === 'email'
                    ? <span className="text-sky-700 dark:text-sky-300">Canal activo: CORREO — {eventKey === 'welcome' ? 'la bienvenida' : 'el mensaje'} por WhatsApp queda {eventKey === 'welcome' ? 'apagada' : 'apagado'} al guardar.</span>
                    : <span className="text-emerald-700 dark:text-emerald-300">Canal activo: WHATSAPP — {eventKey === 'welcome' ? 'la bienvenida' : 'el mensaje'} por correo queda {eventKey === 'welcome' ? 'apagada' : 'apagado'} al guardar.</span>}
                </p>
                {channel === 'email' && emailCoverage && emailCoverage.total > 0 && (
                  <p className="mt-1 text-xs text-content-3">
                    📊 <span className="font-medium text-content-2">{emailCoverage.with_email} de tus {emailCoverage.total} clientes</span> tienen
                    correo registrado{emailCoverage.with_email / emailCoverage.total < 0.5 && ' — considera pedir el correo en el registro para que llegue a más'}.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className={`transition-opacity duration-200 ${!enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {emailChannel && channel === 'email' ? (
              /* ── Editor del CORREO (canal correo elegido) ── */
              <div className="px-4 sm:px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-2 mb-1">Asunto del correo</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder={emailChannel.defaultSubject}
                    className="block w-full rounded-md border-line shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-2 mb-1">Mensaje del correo</label>
                  <textarea
                    rows={6}
                    value={emailTemplate}
                    onChange={(e) => setEmailTemplate(e.target.value)}
                    className="block w-full rounded-md border-line shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm resize-none"
                    placeholder="Escribe la versión para correo — puede ser más larga y detallada que la de WhatsApp..."
                  />
                  {enabled && !emailTemplate.trim() && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Sin mensaje no se envía nada: escribe el contenido del correo.</p>
                  )}
                  <p className="mt-1 text-xs text-content-3">Las <code>*negritas*</code> estilo WhatsApp también funcionan en el correo.</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-content-3 mb-2">Variables disponibles — clic para insertar:</p>
                  <div className="flex flex-wrap gap-2">
                    {variables.map((v) => (
                      <button key={v.key} onClick={() => insertEmailVariable(v.key)}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200 transition-colors dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30"
                        title={`${v.label} (ej: ${v.example})`}>
                        <code className="mr-1">{v.key}</code>
                        <span className="text-sky-500">→ {v.example}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (<>
            {/* Campo extra (alert_phone / threshold) */}
            {extraField && (
              <div className="px-4 sm:px-6 py-4 border-b border-line">
                <label className="block text-sm font-medium text-content-2 mb-1">{extraField.label}</label>
                <input
                  type={extraField.kind === 'number' ? 'number' : 'tel'}
                  value={extraValue}
                  onChange={(e) => setExtraValue(e.target.value)}
                  placeholder={extraField.placeholder}
                  className="block w-full sm:w-72 rounded-md border-line shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
                />
                {extraField.help && <p className="mt-1 text-xs text-content-3">{extraField.help}</p>}
              </div>
            )}

            {/* Editor */}
            <div className="px-4 sm:px-6 py-5 space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-content-2">Mensaje</label>
                <button onClick={handleReset} className="inline-flex items-center text-xs text-content-3 hover:text-content-2" title="Restaurar predeterminado">
                  <RotateCcw className="h-3 w-3 mr-1" /> Restaurar
                </button>
              </div>
              <textarea
                rows={4}
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                className="block w-full rounded-md border-line shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm resize-none"
                placeholder="Escribe el mensaje..."
              />
              <div>
                <p className="text-xs font-medium text-content-3 mb-2">Variables disponibles — clic para insertar:</p>
                <div className="flex flex-wrap gap-2">
                  {variables.map((v) => (
                    <button key={v.key} onClick={() => insertVariable(v.key)}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors"
                      title={`${v.label} (ej: ${v.example})`}>
                      <code className="mr-1">{v.key}</code>
                      <span className="text-emerald-500">→ {v.example}</span>
                    </button>
                  ))}
                </div>
                {variables.some((v) => v.aliases?.length) && (
                  <p className="text-xs text-content-3 mt-2">
                    También funcionan:{' '}
                    {variables.filter((v) => v.aliases?.length).map((v, i) => (
                      <span key={v.key}>{i > 0 && ' · '}<code>{v.aliases!.join(', ')}</code> = <code>{v.key}</code></span>
                    ))}
                  </p>
                )}
              </div>
              <div className="flex justify-between items-center text-xs text-content-3">
                <span>{template.length} caracteres</span>
                {template.length > 500 && <span className="text-amber-500">Mensajes largos pueden truncarse en WhatsApp</span>}
              </div>
            </div>

            {/* Imágenes adjuntas (hasta 3, rotación aleatoria) — solo eventos que las permiten (bienvenida) */}
            {allowImage && (
              <div className="px-4 sm:px-6 py-5 border-t border-line space-y-3">
                <div>
                  <p className="text-sm font-medium text-content-2">Imágenes adjuntas (opcional · hasta {MAX_IMAGES})</p>
                  <p className="text-xs text-content-3 mt-0.5">
                    Si agregas imágenes, el mensaje se envía como imagen con el texto de pie de foto.
                    Con 2 o 3 imágenes se elige una <strong>al azar</strong> en cada envío.
                  </p>
                </div>
                {images.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {images.map((url, i) => (
                      <div key={url + i} className="relative">
                        <img src={url} alt={`Imagen ${i + 1}`} className="h-20 w-20 object-cover rounded-md border border-line" />
                        <button
                          onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow hover:bg-red-700"
                          title="Quitar imagen">×</button>
                      </div>
                    ))}
                  </div>
                )}
                {images.length < MAX_IMAGES && (
                  <ImageUpload
                    key={images.length}
                    onImageChange={(url) => { if (url) setImages((prev) => (prev.length < MAX_IMAGES ? [...prev, url] : prev)); }}
                    title={`Agregar imagen (${images.length}/${MAX_IMAGES})`}
                    description="Sube o arrastra una imagen, o pega una URL"
                  />
                )}
              </div>
            )}
            </>)}
          </div>

          {/* Preview — burbuja de WhatsApp o correo real de marca, según el canal */}
          <div className="px-4 sm:px-6 py-4 border-t border-line bg-surface-2">
            {emailChannel && channel === 'email' ? (<>
              <button onClick={loadEmailPreview} disabled={emailPreviewLoading}
                className="inline-flex items-center text-sm font-medium text-content-2 hover:text-content disabled:opacity-50">
                <Eye className="h-4 w-4 mr-1.5" />
                {emailPreviewLoading ? 'Generando...' : emailPreviewHtml !== null ? 'Ocultar preview' : 'Ver cómo llega el correo'}
              </button>
              {emailPreviewHtml !== null && (
                <div className="mt-4 animate-fade-in-up rounded-lg border border-line overflow-hidden bg-white">
                  <iframe title="Preview del correo" srcDoc={emailPreviewHtml} sandbox="" className="w-full" style={{ height: 520, border: 0 }} />
                </div>
              )}
            </>) : (<>
              <button onClick={() => setShowPreview(!showPreview)} className="inline-flex items-center text-sm font-medium text-content-2 hover:text-content">
                <Eye className="h-4 w-4 mr-1.5" />{showPreview ? 'Ocultar preview' : 'Ver preview del mensaje'}
              </button>
              {showPreview && (
                <div className="mt-4 animate-fade-in-up">
                  <WhatsAppPreviewBubble message={previewMessage} />
                </div>
              )}
            </>)}
          </div>

          <SaveBar saving={saving} hasChanges={hasChanges} onSave={handleSave} />
        </div>

        <StepGuide description={stepGuide.description} steps={stepGuide.steps} className="animate-fade-in-up" />

        <RequirementsChecklist
          requirements={emailChannel && channel === 'email' ? [
            { label: 'Mensaje activado', ok: enabled },
            { label: 'Correo redactado', ok: emailTemplate.trim().length > 10 },
          ] : [
            { label: 'WhatsApp vinculado', ok: wahaConnected === true, checking: isCheckingWaha, actionLabel: 'Vincular', onAction: () => navigate('/dashboard/whatsapp') },
            { label: 'Mensaje activado', ok: enabled },
            { label: 'Plantilla configurada', ok: template.trim().length > 10 },
            ...(extraField ? [{ label: extraField.label, ok: extraValue.trim().length > 0 }] : []),
          ]}
          readyMessage={requirementsReady}
          className="animate-fade-in-up"
        />
      </div>
    </Layout>
  );
};

export default EventMessageEditor;
